<template>
  <modal-component
    :is-displayed="isEditModalOpened"
    :no-padding="true"
    :title="$t('ParkingSpaces.EditTitle')"
    @toggle-modal="$emit('toggle-modal')"
  >
    <map-location-selector
      v-if="isMapLocationModalOpened"
      :coordinates-prop="{
        lat: parkingSpace.latitude,
        lng: parkingSpace.longitude,
      }"
      :is-map-location-modal-opened="isMapLocationModalOpened"
      @toggle-modal="toggleMapLocationModal"
      @get-coordinates="getMarkerCoordinates"
    />
    <form @submit.prevent="handleSubmit">
      <form-wrapper :is-form-in-modal="true">
        <form-divider>
          <input-field
            v-model="parkingSpace.name"
            :error-message="v$?.parkingSpace['name']?.$errors[0]?.$message"
            :is-error="v$?.parkingSpace['name']?.$error"
            :label="$t('ParkingSpaces.Name')"
            :required="true"
          />
          <input-field
            v-model="parkingSpace.city"
            :error-message="v$?.parkingSpace['city']?.$errors[0]?.$message"
            :is-error="v$?.parkingSpace['city']?.$error"
            :label="$t('ParkingSpaces.City')"
            :required="true"
          />
          <input-field
            v-model="parkingSpace.street"
            :error-message="v$?.parkingSpace['street']?.$errors[0]?.$message"
            :is-error="v$?.parkingSpace['street']?.$error"
            :label="$t('ParkingSpaces.Street')"
            :required="true"
          />
          <input-field
            v-model="parkingSpace.streetNumber"
            :error-message="
              v$?.parkingSpace['streetNumber']?.$errors[0]?.$message
            "
            :is-error="v$?.parkingSpace['streetNumber']?.$error"
            :label="$t('ParkingSpaces.StreetNumber')"
            :required="true"
            type="number"
          />
          <div
            class="grid grid-cols-2 gap-6"
            style="grid-template-columns: auto min-content"
          >
            <div>
              <input-field
                v-model="parkingSpace.latitude"
                :error-message="
                  v$?.parkingSpace['latitude']?.$errors[0]?.$message
                "
                :is-error="v$?.parkingSpace['latitude']?.$error"
                :label="$t('ParkingSpaces.Latitude')"
                :required="true"
              />
              <input-field
                v-model="parkingSpace.longitude"
                :error-message="
                  v$?.parkingSpace['longitude']?.$errors[0]?.$message
                "
                :is-error="v$?.parkingSpace['longitude']?.$error"
                :label="$t('ParkingSpaces.Longitude')"
                :required="true"
              />
            </div>
            <div class="pb-3 pt-10">
              <the-button
                :icon="['fas', 'earth-europe']"
                button-type="primaryButton"
                type="button"
                @execute-method="toggleMapLocationModal"
              />
            </div>
          </div>
          <checkbox-input
            v-model="parkingSpace.navigable"
            :is-checked="parkingSpace.navigable"
            :label="$t('ParkingSpaces.Navigable')"
          />
          <checkbox-input
            v-model="parkingSpace.visible"
            :is-checked="parkingSpace.visible"
            :label="$t('ParkingSpaces.Visible')"
          />
          <checkbox-input
            v-model="parkingSpace.active"
            :is-checked="parkingSpace.active"
            :label="$t('ParkingSpaces.Active')"
          />
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form-wrapper>
    </form>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import { ParkingSpace } from "@/modules/Parkom/Submodules/ParkingSpaces/Services/ParkingSpaceTypes";
import InputField from "@/core/Components/FormElements/InputField.vue";
import CheckboxInput from "@/core/Components/FormElements/CheckboxInput.vue";
import MapLocationSelector from "@/core/Components/MapLocationSelector.vue";
import TheButton from "@/core/Components/TheButton.vue";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import ParkingSpaceService from "@/modules/Parkom/Submodules/ParkingSpaces/Services/ParkingSpaceService";
import useValidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default defineComponent({
  components: {
    CheckboxInput,
    InputField,
    ButtonWrapper,
    FormDivider,
    FormWrapper,
    ModalComponent,
    MapLocationSelector,
    TheButton,
  },
  props: {
    isEditModalOpened: Boolean,
    parkingSpaceProp: {
      type: Object as PropType<ParkingSpace>,
      required: true,
    },
    refreshData: {
      type: Function,
      required: true,
    },
  },
  emits: ["toggle-modal"],
  data() {
    return {
      isSubmitLoading: false,
      isMapLocationModalOpened: false,
      parkingSpace: {} as ParkingSpace,
      parkingSpaceService: new ParkingSpaceService(),
      toast: useToast(),
      v$: useValidate(),
    };
  },
  validations() {
    return {
      parkingSpace: {
        name: {
          required,
        },
        city: {
          required,
        },
        street: {
          required,
        },
        streetNumber: {
          required,
        },
        latitude: {
          required,
        },
        longitude: {
          required,
        },
      },
    };
  },
  created() {
    this.parkingSpace = JSON.parse(JSON.stringify(this.parkingSpaceProp));
    console.log(this.parkingSpace);
  },
  methods: {
    async handleSubmit() {
      await this.v$.$validate();
      if (!this.v$.$error) {
        this.isSubmitLoading = true;
        try {
          const { occupied, unoccupied, ...rest } = this.parkingSpace;
          await this.parkingSpaceService.updateParkingSpace(rest);
          this.toast.success("Parking space updated successfully!");
          this.$emit("toggle-modal");
          await this.refreshData();
        } catch (error) {
          this.toast.error(ErrorMessageFormatter(error));
        } finally {
          this.isSubmitLoading = false;
        }
      }
    },
    toggleMapLocationModal() {
      this.isMapLocationModalOpened = !this.isMapLocationModalOpened;
    },
    getMarkerCoordinates(coordinates: { lat: number; lng: number }) {
      this.parkingSpace.latitude = coordinates.lat;
      this.parkingSpace.longitude = coordinates.lng;
    },
  },
});
</script>

<style scoped></style>
